import React from 'react'
import { CARD_STYLES } from '../../utils/styleUtils.js'

/**
 * Card Component
 * 
 * A reusable card component for consistent styling across the application.
 * Provides standard card layouts with optional header, body, and footer sections.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Card content
 * @param {string} [props.title] - Card title/header text
 * @param {React.ReactNode} [props.header] - Custom header content (overrides title)
 * @param {React.ReactNode} [props.footer] - Footer content
 * @param {string} [props.className] - Additional CSS classes
 * @param {Object} [props.style] - Inline styles
 * @param {string} [props.variant='default'] - Card variant (default, info, success, warning, danger)
 * @param {boolean} [props.shadow=true] - Whether to show shadow
 * @param {boolean} [props.border=false] - Whether to show border
 * @returns {JSX.Element} The card component
 */
function Card({ 
  children, 
  title, 
  header, 
  footer, 
  className = '', 
  style = {}, 
  variant = 'default',
  shadow = true,
  border = false, 
}) {
  const cardClasses = [
    'card',
    shadow ? 'shadow-sm' : '',
    border ? 'border' : 'border-0',
    className,
  ].filter(Boolean).join(' ')

  const cardStyle = {
    ...CARD_STYLES.base,
    ...style,
  }

  const getVariantStyle = () => {
    switch (variant) {
      case 'info':
        return { background: 'linear-gradient(to right, #d1ecf1, #bee5eb)' }
      case 'success':
        return { background: 'linear-gradient(to right, #d1e7dd, #badbcc)' }
      case 'warning':
        return { background: 'linear-gradient(to right, #fff3cd, #ffeaa7)' }
      case 'danger':
        return { background: 'linear-gradient(to right, #f8d7da, #f1aeb5)' }
      default:
        return {}
    }
  }

  return (
    <div 
      className={cardClasses} 
      style={{...cardStyle, ...getVariantStyle()}}
    >
      {(header || title) && (
        <div className="card-header fw-bold border-0" style={{ background: 'transparent' }}>
          {header || title}
        </div>
      )}
      
      <div className="card-body">
        {children}
      </div>
      
      {footer && (
        <div className="card-footer border-0" style={{ background: 'transparent' }}>
          {footer}
        </div>
      )}
    </div>
  )
}

export default React.memo(Card)
